import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { ArrowCheckIcon } from '@sharedCR/coreDesignSystem/icons';
import { applicationApi } from 'app/services/api';
import axios from 'axios';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconDirection } from 'constants/coreDesignSystem';
import { FeatureFlag } from 'constants/featureFlags';
import useAbortController from 'hooks/useAbortController';
import { getDefaultErrorMessage } from 'utils/axios';
import { logger } from 'utils/logger';
import { handleLoanProgramsOptionsDetails } from 'utils/programs';

import {
  selectIsOBEngineType,
  selectProgramAndPricingApplicationData,
} from 'store/loanApplications/selectors';
import { selectLLPATypesValuesWithDeletedPostfix } from 'store/programs/selectors';
import { selectPricingConfiguration } from 'store/system/selectors';

import { ColumnDefShape } from '../../ProgramsAndPricing.types';
import ProgramDetails from '../ProgramDetails';
import ProgramSummary from '../ProgramSummary';

import { DEBOUNCE_DELAY_MS, RatesheetStatus } from './ProgramAccordion.constants';
import { getAccordionStyles } from './ProgramAccordion.styles';
import { getCurrentLoanProgramOption, getIsProgramExpired } from './ProgramAccordion.utils';

const ProgramAccordion = ({
  atParPayloadRef,
  id,
  columnsDefs,
  loanProgramOptions,
  programDetails,
  selectedLoanProgramOption,
  onSelectLoanProgramOption,
  pendingChanges,
  registerAccordion,
  observerProps,
}) => {
  const { applicationId } = useParams();

  const isLazyLoadPricingEnabled = useFeatureToggle(FeatureFlag.EnableLazyLoadPricing);

  const application = useSelector(selectProgramAndPricingApplicationData);
  const llpaTypesValues = useSelector(selectLLPATypesValuesWithDeletedPostfix);
  const isOBEngineType = useSelector(selectIsOBEngineType);
  const { enableLocking: isEnableLocking } = useSelector(selectPricingConfiguration);

  const { getAbortSignal, resetAbortSignal, emitAbort } = useAbortController();

  const isLazyLoadEnabled = isLazyLoadPricingEnabled && isOBEngineType;
  const isProgramEligible = !!programDetails?.eligibility?.isAllowed;

  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isShownDetails, setIsShownDetails] = useState(false);
  const [ratesheetState, setRatesheetState] = useState({
    status: RatesheetStatus.IDLE,
    data: null,
    error: null,
  });

  const { currentLoanProgramOption, isSelectedLoanProgramOption } = useMemo(
    () =>
      getCurrentLoanProgramOption({
        loanProgramOptions,
        llpaTypesValues,
        selectedLoanProgramOption,
        pendingChanges,
        application,
        programDetails,
        isOBEngineType,
      }),
    [
      llpaTypesValues,
      loanProgramOptions,
      selectedLoanProgramOption,
      application,
      pendingChanges,
      programDetails,
      isOBEngineType,
    ],
  );

  const isProgramExpired = getIsProgramExpired({
    loanOption: currentLoanProgramOption,
    isEnableLocking,
  });

  const { programId } = currentLoanProgramOption ?? {};

  const { isAccordionVisible, height, isShownSummary } = observerProps;

  const accordionRef = useCallback(
    element => {
      registerAccordion(id, element);
    },
    [id, registerAccordion],
  );

  const isPendingNewProgram = _.get(pendingChanges, 'programId.newValue') === programId;
  const isPendingInitProgram = _.get(pendingChanges, 'programId.initialValue') === programId;
  const isPendingProgramChange = isPendingNewProgram && !isPendingInitProgram;

  const fetchProgramRatesheet = useCallback(() => {
    const atParContext = atParPayloadRef?.current;

    if (!isLazyLoadEnabled || !isProgramEligible || _.isNil(atParContext) || _.isNil(programId)) {
      return;
    }

    emitAbort();
    resetAbortSignal();

    setRatesheetState(prev => ({ ...prev, status: RatesheetStatus.PENDING, error: null }));

    applicationApi
      .getProgramRatesheet({
        applicationId,
        signal: getAbortSignal(),
        payload: atParContext.payload,
        requestId: atParContext.requestId,
        programId,
      })
      .then(response => {
        const { details } = response.data || {};

        const processedDetails = handleLoanProgramsOptionsDetails({
          ...details,
          vendorType: atParContext.engineType,
          loanProgramsOptions: atParContext.loanProgramsOptions,
          ineligibleOptionsData: atParContext.ineligibleOptionsData,
        });

        setRatesheetState(prev => ({
          ...prev,
          data: processedDetails,
          status: RatesheetStatus.FULFILLED,
        }));
      })
      .catch(err => {
        if (!axios.isCancel(err)) {
          setRatesheetState(prev => ({
            ...prev,
            error: getDefaultErrorMessage(err),
            status: RatesheetStatus.REJECTED,
          }));

          logger.error(err);
        }
      });
  }, [
    applicationId,
    atParPayloadRef,
    emitAbort,
    getAbortSignal,
    isProgramEligible,
    isLazyLoadEnabled,
    programId,
    resetAbortSignal,
  ]);

  const onChangeAccordion = useCallback(
    (_, isExpanded) => {
      setIsExpanded(isExpanded);

      if (isExpanded && isLazyLoadEnabled && ratesheetState.status === RatesheetStatus.IDLE) {
        fetchProgramRatesheet();
      }
    },
    [fetchProgramRatesheet, isLazyLoadEnabled, ratesheetState.status],
  );

  useEffect(() => {
    if (!isExpanded && isAccordionVisible) {
      const timer = setTimeout(() => setIsShownDetails(false), DEBOUNCE_DELAY_MS); // Delay to preserve accordion animation

      return () => clearTimeout(timer);
    }

    setIsShownDetails(isExpanded);
  }, [isExpanded, isAccordionVisible]);

  return (
    <Accordion
      data-id={id}
      data-is-pending={isPendingProgramChange}
      data-test="program-accordion"
      ref={accordionRef}
      sx={getAccordionStyles({
        isAccordionVisible,
        height,
        isSelected: isSelectedLoanProgramOption && !isPendingProgramChange,
      })}
      onChange={onChangeAccordion}
    >
      <AccordionSummary
        aria-controls={`panel-${id}-content`}
        className="w-full"
        expandIcon={<ArrowCheckIcon direction={IconDirection.DOWN} />}
        id={`panel-${id}-summary`}
      >
        {isShownSummary && (
          <ProgramSummary
            columnsDefs={columnsDefs}
            currentLoanProgramOption={currentLoanProgramOption}
            isPendingInitProgram={isPendingInitProgram}
            isPendingNewProgram={isPendingNewProgram}
            isPendingProgramChange={isPendingProgramChange}
            isProgramExpired={isProgramExpired}
            isSelectedLoanProgramOption={isSelectedLoanProgramOption}
            pendingChanges={pendingChanges}
            programDetails={programDetails}
          />
        )}
      </AccordionSummary>

      <AccordionDetails>
        {isShownDetails && (
          <ProgramDetails
            currentLoanProgramOption={currentLoanProgramOption}
            isProgramExpired={isProgramExpired}
            isSelectedLoanProgramOption={isSelectedLoanProgramOption}
            pendingChanges={pendingChanges}
            programDetails={programDetails}
            ratesheetData={ratesheetState.data}
            ratesheetError={ratesheetState.error}
            ratesheetStatus={isLazyLoadEnabled ? ratesheetState.status : null}
            selectedLoanProgramOption={selectedLoanProgramOption}
            onRetryRatesheet={fetchProgramRatesheet}
            onSelectLoanProgramOption={onSelectLoanProgramOption}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
};

ProgramAccordion.propTypes = {
  columnsDefs: PropTypes.arrayOf(ColumnDefShape).isRequired,
  loanProgramOptions: PropTypes.array.isRequired,
  programDetails: PropTypes.object.isRequired,
  observerProps: PropTypes.shape({
    isAccordionVisible: PropTypes.bool.isRequired,
    height: PropTypes.number.isRequired,
    isShownSummary: PropTypes.bool.isRequired,
  }).isRequired,
  registerAccordion: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  onSelectLoanProgramOption: PropTypes.func.isRequired,
  atParPayloadRef: PropTypes.shape({ current: PropTypes.object }),
  pendingChanges: PropTypes.object,
  selectedLoanProgramOption: PropTypes.object,
};

export default memo(ProgramAccordion);
